import { useState, useRef, useMemo } from 'react'
import { supabase } from '../../../api/supabase'
import { PdfExtractError } from '../../../api/commercialInvoicePdf'
import { useNavigate } from 'react-router-dom'
import { generateCDSExcel } from '../../../api/cdsExporter'
import { validateHSCodesWithHMRC } from '../../../api/hmrcValidator'
import { useCommercialInvoices, roundCartonsForCustoms } from '../../../hooks/useCommercialInvoices'
import { normaliseCiParsed, withDeclaredTotals, ciLineTotals, ciDiscrepancies, round2 } from '../../../lib/ciNormalise'
import { useHsCodeRules } from '../../../hooks/useHsCodeRules'
import type { HsCodeRule } from '../../../hooks/useHsCodeRules'
import { createTemplate, updateTemplate, touchTemplateUsage, findExactCell, deriveHeaderAnchor } from '../../../api/invoiceTemplates'
import type { InvoiceTemplate } from '../../../api/invoiceTemplates'
import { buildInvoicePivot, parseCommercialInvoiceFile, getFileType, readMultiFileRows, AmbiguousTemplateMatch } from '../../../api/parseCommercialInvoiceFile'
import type { SheetInfo } from '../../../api/parseCommercialInvoiceFile'
import { useCustomersByRole } from '../../../hooks/useCustomersByRole'
import CustomerCombobox from '../../../components/CustomerCombobox'
import { useStickyHScroll } from '../../../hooks/useStickyHScroll'
import { StickyHScrollbar } from '../../../components/StickyHScrollbar'
import type { InvoiceParsed, InvoiceItem, InvoiceHeader, FieldMappings } from '../../../api/claudeInvoiceParser'
import type { HSCodeValidation } from '../../../api/hmrcValidator'
import { isValidIsoCountry } from '../../../lib/cds/countries'

type Step = 'select-customer' | 'upload' | 'extracting' | 'parsing' | 'validating' | 'generating-pivot' | 'review' | 'duplicate-check' | 'saving' | 'done'

interface StripSuggestion {
  field: string
  label: string
  kind: 'strip' | 'relocate'
  strip_before?: string
  strip_after?: string
  // 'relocate' only -- the sheet cell where the reviewer's corrected value
  // actually lives, once the correction turned out not to be a substring of
  // the originally-mapped cell (the template was pointing at the wrong cell
  // entirely, e.g. invoice_no).
  row?: number
  col?: number
}

function blankInvoiceParsed(): InvoiceParsed {
  return {
    header: {
      invoice_no: null, job_ref: null, bill_no: null, declare_method: null, trade_term: null,
      shipper: null, shipper_address: null, consignee: null, consignee_address: null,
      country_of_origin: 'CN', destination_country: 'GB', invoice_date: null, currency: 'USD',
      total_amount: null, gross_weight_kg: null, net_weight_kg: null, packages: null,
      total_cbm: null, incoterms: null, vessel_flight: null, port_of_loading: null,
      port_of_discharge: null, vat_deferred: false, use_cyuk_dan: false, eori: null, vat_no: null,
    },
    items: [],
    pivot: [],
    confidence: 0,
  }
}

export default function InvoiceUpload() {
  const navigate = useNavigate()
  const { createInvoiceFromAI, checkDuplicateInvoice } = useCommercialInvoices()
  const [step, setStep] = useState<Step>('select-customer')
  const [file, setFile] = useState<File | null>(null)
  // Z42.2: optional second file when the Invoice and Packing List are two
  // separate Excel files rather than 2 sheets in one workbook -- both get
  // concatenated into the same rows[][] grid readExcelRowsMultiSheet would
  // have produced from a single combined file (see readMultiFileRows).
  const [plFile, setPlFile] = useState<File | null>(null)
  const [sheetsDetected, setSheetsDetected] = useState<SheetInfo[] | null>(null)
  const [extracted, setExtracted] = useState<InvoiceParsed | null>(null)
  const [edited, setEdited] = useState<InvoiceParsed | null>(null)
  const [validations, setValidations] = useState<HSCodeValidation[]>([])
  const [duplicate, setDuplicate] = useState<any>(null)
  const [error, setError] = useState<string>('')
  const [manualFallbackNotice, setManualFallbackNotice] = useState<string>('')
  const [progress, setProgress] = useState<string>('')
  const [pivotGenerated, setPivotGenerated] = useState(false)
  const [totalMismatch, setTotalMismatch] = useState<string | null>(null)
  const [useCyukDan, setUseCyukDan] = useState(false)
  const [usedTemplateId, setUsedTemplateId] = useState<string | null>(null)
  const [usedTemplateName, setUsedTemplateName] = useState<string | null>(null)
  const [templatesConsidered, setTemplatesConsidered] = useState<number | null>(null)
  const [ambiguousCandidates, setAmbiguousCandidates] = useState<InvoiceTemplate[] | null>(null)
  const [fieldMappingsUsed, setFieldMappingsUsed] = useState<FieldMappings | null>(null)
  // Template-replay path only: the template's own field_mappings (to merge a
  // strip rule into and updateTemplate with) and the pre-strip raw cell value
  // for each mapped header field (to detect when a manual edit is really just
  // "raw cell has label noise around the value the AI/template already found").
  const [templateFieldMappings, setTemplateFieldMappings] = useState<FieldMappings | null>(null)
  const [rawHeaderCells, setRawHeaderCells] = useState<Record<string, unknown> | null>(null)
  const [stripSuggestions, setStripSuggestions] = useState<StripSuggestion[] | null>(null)
  // Template-replay path only: header fields the hybrid validator flagged as
  // wrong and patched with a targeted single-field AI read (see
  // invoiceFieldValidation.ts + parseCommercialInvoiceFile.ts) -- offered to
  // staff as "save this correction back into the template" before the next
  // upload from this customer hits the same stale mapping again.
  const [aiCorrectedFields, setAiCorrectedFields] = useState<Record<string, unknown> | null>(null)
  const [aiCorrectionSaving, setAiCorrectionSaving] = useState(false)
  const [showTrainingModal, setShowTrainingModal] = useState(false)
  const [trainingSaving, setTrainingSaving] = useState(false)
  const [trainingError, setTrainingError] = useState('')
  // Z73.3: pre-fill from ?job_ref=... -- the eCustoms preview's "Upload CI now" link lands here
  // with the job's ref already known, so staff don't have to retype it (and risk a typo that'd
  // make the CI invisible to that job's declaration all over again).
  const [jobRef, setJobRef] = useState(() => new URLSearchParams(window.location.search).get('job_ref') || '')
  const [driveFolderUrl, setDriveFolderUrl] = useState('')
  const [sourceType, setSourceType] = useState<'local' | 'drive'>('local')

  // HS Code rules integration
  const { fetchMatchingRules, createRule: createHsRule } = useHsCodeRules()
  const [hsRuleSuggestions, setHsRuleSuggestions] = useState<HsCodeRule[]>([])
  const [dismissedRules, setDismissedRules] = useState<Set<string>>(new Set())
  const [showRulesModal, setShowRulesModal] = useState(false)
  const [saveRulePrompt, setSaveRulePrompt] = useState<{ from: string; to: string } | null>(null)
  const [saveRuleReason, setSaveRuleReason] = useState('')
  const [bulkHsPrompt, setBulkHsPrompt] = useState<{ oldCode: string; newCode: string; matchingIndices: number[]; sourceIdx: number } | null>(null)
  const hsInputFocusRef = useRef<string | null>(null)

  // Customer selection state -- CY UK's own billing customer for this invoice,
  // not to be confused with the shipper/consignee parsed from the file itself.
  // Required before file upload: template lookup is keyed on customer_id.
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('')
  const { customers } = useCustomersByRole('Cus')
  const selectedCustomer = customers.find(c => c.id === selectedCustomerId) || null
  const fileRef = useRef<HTMLInputElement>(null)
  const plFileRef = useRef<HTMLInputElement>(null)

  const editedData = edited || extracted
  const { scrollProps: itemsScrollProps, contentRef: itemsTableRef, bar: itemsScrollBar } = useStickyHScroll([editedData?.items])

  const pivot = useMemo(() => {
    if (!editedData?.items) return []
    const map = new Map<string, any>()
    for (const item of editedData.items) {
      const key = item.hs_code || 'UNKNOWN'
      if (!map.has(key)) {
        map.set(key, {
          hs_code: key,
          goods_description: item.goods_description || '',
          pcs: 0,
          ctns: 0,
          gross_weight: 0,
          net_weight: 0,
          amount: 0,
        })
      }
      const p = map.get(key)!
      p.pcs += item.pcs || 0
      p.ctns += item.ctns || 0
      p.gross_weight = round2(p.gross_weight + (item.gross_weight || 0))
      p.net_weight = round2(p.net_weight + (item.net_weight || 0))
      p.amount = round2(p.amount + (item.amount || 0))
    }
    return Array.from(map.values())
  }, [editedData])

  async function processFile(fileToProcess: File, plFileToProcess: File | null, resolveOptions?: { forceTemplateId?: string; forceAI?: boolean }) {
    setError('')
    setManualFallbackNotice('')
    setUsedTemplateId(null)
    setUsedTemplateName(null)
    setTemplatesConsidered(null)
    setAmbiguousCandidates(null)
    setFieldMappingsUsed(null)
    setTemplateFieldMappings(null)
    setRawHeaderCells(null)
    setStripSuggestions(null)
    setAiCorrectedFields(null)
    setSheetsDetected(null)

    const fileType = getFileType(fileToProcess)
    if (fileType === 'unsupported') {
      setError('Unsupported file type. Please upload a PDF or Excel file (.pdf, .xlsx, .xls)')
      return
    }

    setStep('extracting')
    setPivotGenerated(false)
    try {
      let result: InvoiceParsed & {
        templateId?: string | null; templateName?: string | null
        templateFieldMappings?: FieldMappings | null; raw_header_cells?: Record<string, unknown>
        templatesConsidered?: number
        aiCorrectedFields?: Record<string, unknown>
        sheets?: SheetInfo[]
      }

      try {
        setStep('parsing')
        setProgress(fileType === 'pdf' ? 'Extracting & parsing PDF with AI...' : 'Checking for a saved template, or parsing with AI...')
        result = await parseCommercialInvoiceFile(plFileToProcess ? [fileToProcess, plFileToProcess] : fileToProcess, selectedCustomerId || undefined, resolveOptions)
      } catch (parseErr) {
        if (parseErr instanceof AmbiguousTemplateMatch) {
          // Pause here -- staff must pick a template (or AI) before we parse
          // anything. Not a failure, so no manual-fallback banner.
          setAmbiguousCandidates(parseErr.candidates)
          setStep('upload')
          return
        }
        // Never lose the record: if AI/template extraction fails outright, fall
        // through to a blank form the staff fills in by hand.
        console.error('[processFile] Parse failed, falling back to manual entry:', parseErr)
        result = blankInvoiceParsed()
        const reason = parseErr instanceof PdfExtractError ? `${parseErr.userMessage} [${parseErr.message.slice(0, 220)}]`
          : parseErr instanceof Error ? parseErr.message : 'an unexpected error occurred'
        setManualFallbackNotice(`AI could not extract this file: ${reason}. Please enter the details manually below.`)
      }

      if (!result.items?.length && !result.header?.invoice_no) {
        setManualFallbackNotice(prev => prev || 'AI could not extract meaningful data from this file — please review and fill in the fields manually.')
      }

      setUsedTemplateId(result.templateId ?? null)
      setUsedTemplateName(result.templateName ?? null)
      setTemplatesConsidered(result.templatesConsidered ?? null)
      setFieldMappingsUsed(result.field_mappings ?? null)
      setTemplateFieldMappings(result.templateFieldMappings ?? null)
      setRawHeaderCells(result.raw_header_cells ?? null)
      setAiCorrectedFields(result.aiCorrectedFields ?? null)
      setSheetsDetected(result.sheets && result.sheets.length > 0 ? result.sheets : null)

      // Z98: 2 dp per line, CTNS 0 -> 1 (original kept) -- before anything is displayed or edited
      const normalised = withDeclaredTotals(normaliseCiParsed(result))
      setExtracted(normalised)
      setEdited(normalised)
      setUseCyukDan(result.header.use_cyuk_dan || false)

      setStep('validating')
      setProgress('Validating HS codes with HMRC...')
      const hsCodes = result.items?.map(i => i.hs_code).filter(c => c) as string[] || []

      if (hsCodes.length > 0) {
        try {
          const validationResults = await validateHSCodesWithHMRC(hsCodes)
          setValidations(validationResults)
        } catch (validationErr) {
          console.error(`[processFile] HMRC validation error:`, validationErr)
          setValidations([])
        }
      } else {
        setValidations([])
      }

      // Load HS code replacement rules for the parsed codes
      const parsedCodes = result.items.map(i => i.hs_code).filter(Boolean) as string[]
      if (parsedCodes.length > 0) {
        const rules = await fetchMatchingRules(parsedCodes)
        setHsRuleSuggestions(rules)
        setDismissedRules(new Set())
        if (rules.length > 0) setShowRulesModal(true)
      } else {
        setHsRuleSuggestions([])
      }

      setProgress('')
      handleGeneratePivot(result)
    } catch (e: unknown) {
      const errorMsg = e instanceof Error ? e.message : 'Unknown error occurred'
      console.error(`[processFile] Error:`, e)
      setError(errorMsg)
      setStep('upload')
    }
  }

  async function handleExtract() {
    if (!file) return
    await processFile(file, plFile)
  }

  async function resolveAmbiguousTemplate(choice: { templateId: string } | { forceAI: true }) {
    if (!file) return
    setAmbiguousCandidates(null)
    await processFile(file, plFile, 'templateId' in choice ? { forceTemplateId: choice.templateId } : { forceAI: true })
  }

  async function handleExtractFromDrive() {
    if (!driveFolderUrl.trim()) return
    setError('')
    setStep('extracting')
    setProgress('Fetching file from Google Drive...')

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      if (!supabaseUrl) throw new Error('Missing Supabase configuration')
      // fetch-drive-file has verify_jwt on: send the signed-in user's JWT, not the anon/publishable key.
      const accessToken = (await supabase.auth.getSession()).data.session?.access_token
      if (!accessToken) throw new Error('Your session has expired. Please sign in again.')

      const response = await fetch(`${supabaseUrl}/functions/v1/fetch-drive-file`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ driveUrl: driveFolderUrl.trim() })
      })

      const data = await response.json()
      if (!response.ok) {
        throw new Error(data.error || `Drive fetch failed (HTTP ${response.status})`)
      }

      const { fileName, mimeType, base64Content } = data
      const binaryStr = atob(base64Content)
      const bytes = new Uint8Array(binaryStr.length)
      for (let i = 0; i < binaryStr.length; i++) bytes[i] = binaryStr.charCodeAt(i)
      const driveFile = new File([bytes], fileName, { type: mimeType })
      setFile(driveFile)

      await processFile(driveFile, null)
    } catch (e: unknown) {
      const errorMsg = e instanceof Error ? e.message : 'Failed to fetch from Google Drive'
      setError(errorMsg)
      setStep('upload')
    }
  }

  function handleGeneratePivot(overrideData?: InvoiceParsed) {
    const data = overrideData || editedData
    if (!data?.items) return
    setStep('generating-pivot')
    setPivotGenerated(true)
    setTotalMismatch(null)

    const items = data.items
    const pivotData = buildInvoicePivot(items)

    const sourcePcs = items.reduce((sum, item) => sum + (item.pcs || 0), 0)
    const sourceCtns = items.reduce((sum, item) => sum + (item.ctns || 0), 0)
    const sourceGw = items.reduce((sum, item) => sum + (item.gross_weight || 0), 0)
    const sourceNw = items.reduce((sum, item) => sum + (item.net_weight || 0), 0)
    const sourceAmount = items.reduce((sum, item) => sum + (item.amount || 0), 0)

    const pivotPcs = pivotData.reduce((sum, item) => sum + (item.pcs || 0), 0)
    const pivotCtns = pivotData.reduce((sum, item) => sum + (item.ctns || 0), 0)
    const pivotGw = pivotData.reduce((sum, item) => sum + (item.gross_weight || 0), 0)
    const pivotNw = pivotData.reduce((sum, item) => sum + (item.net_weight || 0), 0)
    const pivotAmount = pivotData.reduce((sum, item) => sum + (item.amount || 0), 0)

    const tolerance = 0.01
    const mismatches: string[] = []

    if (Math.abs(sourcePcs - pivotPcs) > tolerance) mismatches.push('Total PCS')
    if (Math.abs(sourceCtns - pivotCtns) > tolerance) mismatches.push('Total CTNS')
    if (Math.abs(sourceGw - pivotGw) > tolerance) mismatches.push('Total GW (kg)')
    if (Math.abs(sourceNw - pivotNw) > tolerance) mismatches.push('Total NW (kg)')
    if (Math.abs(sourceAmount - pivotAmount) > tolerance) mismatches.push('Total Amount')

    if (mismatches.length > 0) {
      setTotalMismatch(mismatches.join(', '))
    }

    setTimeout(() => setStep('review'), 300)
  }


  async function handleCheckDuplicate() {
    if (!editedData?.header.invoice_no) {
      setError('Invoice number is required')
      return
    }
    const dup = await checkDuplicateInvoice(editedData.header.invoice_no)
    if (dup) {
      setDuplicate(dup)
      setStep('duplicate-check')
    } else {
      await handleSave()
    }
  }

  function updateHeader(field: keyof InvoiceHeader, value: any) {
    if (!editedData) return
    setEdited(withDeclaredTotals({
      ...editedData,
      header: { ...editedData.header, [field]: value }
    }))
  }

  function updateItem(idx: number, field: keyof InvoiceItem, value: any) {
    if (!editedData) return
    const newItems = [...editedData.items]
    let v = value
    if ((field === 'gross_weight' || field === 'net_weight' || field === 'amount') && typeof v === 'number') v = round2(v)
    newItems[idx] = { ...newItems[idx], [field]: v }
    // Z98: a manual CTNS edit clears the auto-adjusted marker, so the cell goes back to its normal colour
    if (field === 'ctns') newItems[idx].ctns_original = null
    setEdited(withDeclaredTotals({ ...editedData, items: newItems }))
  }

  function addItem() {
    if (!editedData) return
    const newItem: InvoiceItem = {
      line_no: (editedData.items?.length || 0) + 1,
      hs_code: null,
      hs_code_suggestion: null,
      hs_code_confidence: 0,
      goods_description: '',
      pcs: null,
      ctns: null,
      gross_weight: null,
      net_weight: null,
      amount: null,
      currency: editedData.header.currency,
      country_of_origin: editedData.header.country_of_origin,
      cbm: null,
      unit_value: null,
    }
    setEdited(withDeclaredTotals({
      ...editedData,
      items: [...(editedData.items || []), newItem]
    }))
  }

  function removeItem(idx: number) {
    if (!editedData) return
    setEdited(withDeclaredTotals({
      ...editedData,
      items: editedData.items.filter((_, i) => i !== idx)
    }))
  }

  // Auto-generated (fieldMappingsUsed came back from a fresh AI parse) or the
  // reviewer hand-edited a field that came from an existing template -- either
  // way the customer's template should reflect what actually got saved.
  function wasEdited(): boolean {
    return !!extracted && !!edited && JSON.stringify(extracted) !== JSON.stringify(edited)
  }

  // throwOnError: doSave()'s auto-upsert is a nice-to-have cache and must
  // never block the invoice save, so it swallows failures (default). The
  // explicit "Save Template" action in the training modal has no other
  // effect to fall back on -- silently swallowing there would report
  // success on a save that never happened, so it opts into throwOnError.
  async function upsertTemplateIfNeeded(sampleNotes?: string, throwOnError = false) {
    if (!selectedCustomerId || !editedData) return
    // Only fieldMappingsUsed carries real {row,col} positions -- a value edit
    // on an already-template-parsed invoice doesn't tell us a new cell
    // position, so there's nothing reliable to re-save in that case.
    if (!fieldMappingsUsed) return

    const fileTypeForTemplate = file?.name.toLowerCase().endsWith('.pdf') ? 'pdf' as const
      : file?.name.toLowerCase().endsWith('.xls') ? 'xls' as const : 'xlsx' as const

    try {
      if (usedTemplateId) {
        await updateTemplate(usedTemplateId, { field_mappings: fieldMappingsUsed, sample_notes: sampleNotes })
      } else {
        await createTemplate({
          customer_id: selectedCustomerId,
          name: selectedCustomer ? `${selectedCustomer.code} – auto-created` : 'Auto-created template',
          field_mappings: fieldMappingsUsed,
          file_type: fileTypeForTemplate,
          sample_notes: sampleNotes || `Auto-saved from ${editedData.header.invoice_no || 'invoice'}`,
        })
      }
    } catch (err) {
      console.error('[upsertTemplateIfNeeded] Error:', err)
      if (throwOnError) throw err
    }
  }

  // Template-replay path only: a reviewer's edit sometimes isn't a new value
  // at all -- it's the AI/template having read the whole labeled cell (e.g.
  // "INVOICE NO. : 2026-MF062603") where the clean value is a substring of
  // the raw cell text. Detecting that lets us offer a strip rule instead of
  // just accepting the same noisy read forever.
  // When the correction ISN'T a substring, the mapping is pointing at the
  // wrong cell entirely (e.g. invoice_no mapped to the wrong row) -- re-read
  // the sheet and look for the corrected value elsewhere so the template can
  // be re-anchored instead of silently carrying the same wrong cell forward
  // on every future upload for this customer.
  async function detectTemplateCorrections(): Promise<StripSuggestion[]> {
    if (!rawHeaderCells || !extracted || !edited) return []
    const suggestions: StripSuggestion[] = []
    let rows: any[][] | null = null
    for (const [field, rawVal] of Object.entries(rawHeaderCells)) {
      if (typeof rawVal !== 'string') continue
      const editedVal = (edited.header as any)[field]
      const extractedVal = (extracted.header as any)[field]
      if (typeof editedVal !== 'string' || !editedVal) continue
      if (editedVal === extractedVal) continue // field wasn't actually edited
      if (editedVal === rawVal) continue // no label noise to strip
      const idx = rawVal.toLowerCase().indexOf(editedVal.toLowerCase())
      if (idx === -1) {
        if (!file) continue
        if (rows === null) {
          try { rows = (await readMultiFileRows(plFile ? [file, plFile] : [file])).rows as any[][] } catch { rows = [] }
        }
        const priorSpec = (templateFieldMappings as any)?.[field]
        const nearRow = typeof priorSpec?.row === 'number' ? priorSpec.row : priorSpec?.anchor_row_hint
        const cell = findExactCell(rows, editedVal, nearRow)
        if (cell) {
          suggestions.push({ field, label: field.replace(/_/g, ' '), kind: 'relocate', row: cell.row, col: cell.col })
        }
        continue
      }
      const before = rawVal.slice(0, idx)
      const after = rawVal.slice(idx + editedVal.length)
      if (before.trim() || after.trim()) {
        suggestions.push({
          field,
          label: field.replace(/_/g, ' '),
          kind: 'strip',
          strip_before: before.trim() ? before : undefined,
          strip_after: after.trim() ? after : undefined,
        })
      }
    }
    return suggestions
  }

  async function doSave() {
    if (!editedData) return
    setStep('saving')
    setDuplicate(null)
    try {
      // Z98 -- TWO DELIBERATELY DIFFERENT RULES, do not "fix" them into one:
      //  * Weights and amount: header = sum of the ROUNDED lines. Descartes checks the header against the line
      //    total and rejects any difference, so the two must agree exactly. The invoice's own figure is kept in
      //    stated_* so the 2519.898 -> 2519.90 change can always be explained.
      //  * Packages: header stays as PRINTED on the invoice (178). Customs want the invoice's total package
      //    count; the line CTNS (182 here) are only adjusted per line (0 / half cartons -> 1) and are not the
      //    declared total. Gary confirmed: the 178 vs 182 difference is expected, not an error.
      const t = ciLineTotals(editedData.items)
      const h = editedData.header
      const toSave = {
        ...editedData,
        header: {
          ...h,
          stated_total_amount: h.stated_total_amount ?? h.total_amount, stated_gross_weight_kg: h.stated_gross_weight_kg ?? h.gross_weight_kg, stated_net_weight_kg: h.stated_net_weight_kg ?? h.net_weight_kg,
          total_amount: t.total_amount, gross_weight_kg: t.gross_weight_kg, net_weight_kg: t.net_weight_kg,
        },
      }
      const inv = await createInvoiceFromAI(toSave, jobRef, driveFolderUrl, selectedCustomerId)
      if (usedTemplateId) {
        // Usage is about the parse, not whether the reviewer edited anything
        // afterward -- track it regardless.
        await touchTemplateUsage(usedTemplateId)
      }
      if (fieldMappingsUsed || (usedTemplateId && wasEdited())) {
        await upsertTemplateIfNeeded()
      }
      setStep('done')
      setTimeout(() => navigate(`/customs/invoices/${inv.id}`), 1500)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed')
      setStep('review')
    }
  }

  async function handleSave(overwrite: boolean = false) {
    if (!editedData) return

    if (!overwrite) {
      await handleCheckDuplicate()
      return
    }

    // Only relevant on the template-replay path (fieldMappingsUsed means AI
    // ran fresh this time, which already gets the clean value) -- and only
    // asked once per save cycle (stripSuggestions !== null once resolved).
    if (usedTemplateId && !fieldMappingsUsed && stripSuggestions === null) {
      const suggestions = await detectTemplateCorrections()
      if (suggestions.length > 0) {
        setStripSuggestions(suggestions)
        return
      }
    }

    await doSave()
  }

  async function resolveStripSuggestions(accept: boolean) {
    if (accept && stripSuggestions && usedTemplateId && templateFieldMappings) {
      const merged: any = { ...templateFieldMappings }
      let rows: any[][] | null = null
      for (const s of stripSuggestions) {
        if (s.kind === 'relocate' && typeof s.row === 'number' && typeof s.col === 'number') {
          if (rows === null && file) {
            try { rows = (await readMultiFileRows(plFile ? [file, plFile] : [file])).rows as any[][] } catch { rows = [] }
          }
          merged[s.field] = rows ? deriveHeaderAnchor(rows, { row: s.row, col: s.col }) : { row: s.row, col: s.col }
        } else {
          merged[s.field] = { ...(merged[s.field] || {}), strip_before: s.strip_before, strip_after: s.strip_after }
        }
      }
      try {
        await updateTemplate(usedTemplateId, { field_mappings: merged })
        setTemplateFieldMappings(merged)
      } catch (err) {
        console.error('[resolveStripSuggestions] Error:', err)
      }
    }
    setStripSuggestions([])
    await doSave()
  }

  // Re-anchors the template to wherever the AI-corrected value actually lives
  // in the sheet (same findExactCell + deriveHeaderAnchor "relocate" logic
  // resolveStripSuggestions uses for a manual correction) so the next upload
  // from this customer reads the right cell instead of hitting AI again.
  async function handleSaveAiCorrections() {
    if (!aiCorrectedFields || !usedTemplateId || !templateFieldMappings || !file) {
      setAiCorrectedFields(null)
      return
    }
    setAiCorrectionSaving(true)
    try {
      const rows = (await readMultiFileRows(plFile ? [file, plFile] : [file])).rows as any[][]
      const merged: any = { ...templateFieldMappings }
      for (const [field, value] of Object.entries(aiCorrectedFields)) {
        if (typeof value !== 'string' && typeof value !== 'number') continue
        const priorSpec = (templateFieldMappings as any)?.[field]
        const nearRow = typeof priorSpec?.row === 'number' ? priorSpec.row : priorSpec?.anchor_row_hint
        const cell = findExactCell(rows, String(value), nearRow)
        if (cell) merged[field] = deriveHeaderAnchor(rows, { row: cell.row, col: cell.col })
      }
      await updateTemplate(usedTemplateId, { field_mappings: merged })
      setTemplateFieldMappings(merged)
    } catch (err) {
      console.error('[handleSaveAiCorrections] Error:', err)
    } finally {
      setAiCorrectionSaving(false)
      setAiCorrectedFields(null)
    }
  }

  function handleExportCDS() {
    if (!editedData) return
    generateCDSExcel(editedData.header, pivot, useCyukDan)
  }

  function isLowConfidence(confidence: number): boolean {
    return confidence < 0.7
  }

  function handleOpenTrainingModal() {
    setTrainingError('')
    setShowTrainingModal(true)
  }

  async function handleSaveTemplate() {
    if (!editedData || !selectedCustomerId) return
    setTrainingSaving(true)
    setTrainingError('')
    try {
      await upsertTemplateIfNeeded(`Template created from ${editedData.header.invoice_no || 'invoice'}`, true)
      setShowTrainingModal(false)
      setError('')
      setProgress('✅ Template saved for this customer — future uploads from them will skip AI.')
      setTimeout(() => setProgress(''), 3000)
    } catch (err) {
      const errMsg = err instanceof Error
        ? err.message
        : (err && typeof err === 'object' && 'message' in err && typeof (err as { message: unknown }).message === 'string')
          ? (err as { message: string }).message
          : 'Failed to save template'
      setTrainingError(errMsg)
      console.error('[handleSaveTemplate] Error:', err)
    } finally {
      setTrainingSaving(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <button onClick={() => navigate('/customs/invoices')}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Upload & Parse Invoice</h1>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-8">
          {(['select-customer','upload','extracting','parsing','validating','generating-pivot','review','duplicate-check','saving','done'] as Step[]).map((s, i) => (
            <div key={s} className="flex items-center">
              <div className={"w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium " +
                (step === s ? 'bg-blue-600 text-white' :
                (['extracting','parsing','validating','generating-pivot','review','duplicate-check','saving','done'] as Step[]).indexOf(step) > (['select-customer','upload','extracting','parsing','validating','generating-pivot','review','duplicate-check','saving','done'] as Step[]).indexOf(s) ? 'bg-green-500 text-white' :
                'bg-gray-200 dark:bg-gray-700 text-gray-500')}>
                {i + 1}
              </div>
              {i < 9 && <div className="w-8 h-0.5 bg-gray-200 dark:bg-gray-700" />}
            </div>
          ))}
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
            {error}
          </div>
        )}

        {/* Customer Selection Step */}
        {step === 'select-customer' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Select Customer</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Choose the CY UK customer this invoice is for. Required before upload — the file is parsed against this customer's saved template if one exists.
            </p>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Customer</label>
              <CustomerCombobox value={selectedCustomerId} onChange={setSelectedCustomerId} role="Cus" allowCreate />
            </div>

            <button
              onClick={() => setStep('upload')}
              disabled={!selectedCustomerId}
              className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
            >
              Continue
            </button>
          </div>
        )}

        {selectedCustomer && step !== 'select-customer' && (
          <div className="mb-4 p-4 rounded-lg border bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-400">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold">Customer: {selectedCustomer.code} – {selectedCustomer.name}</p>
              </div>
              <button
                onClick={() => {
                  const wasPastUpload = step !== 'upload'
                  setStep('select-customer')
                  if (wasPastUpload && file) {
                    // Template lookup is customer-keyed -- a stale parse from
                    // the previous customer must not survive a change.
                    setExtracted(null)
                    setEdited(null)
                    setPivotGenerated(false)
                  }
                }}
                className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded transition-colors"
              >
                Change
              </button>
            </div>
          </div>
        )}

        {usedTemplateId && (
          <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-400">
            ✅ Using template: <strong>{usedTemplateName || 'this customer\'s saved template'}</strong> — parsed directly without AI
          </div>
        )}

        {sheetsDetected && sheetsDetected.length > 0 && step !== 'select-customer' && step !== 'upload' && (
          <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-800 rounded-lg text-sm text-gray-600 dark:text-gray-400">
            Detected sheets: {sheetsDetected.map(s => `"${s.name}" → ${s.role === 'invoice' ? 'Invoice' : s.role === 'packingList' ? 'Packing List' : 'unrecognized'}`).join(', ')}
          </div>
        )}

        {!usedTemplateId && templatesConsidered != null && step !== 'select-customer' && step !== 'upload' && (
          <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-blue-700 dark:text-blue-400">
            {templatesConsidered === 0
              ? "This customer has no saved templates yet — parsed with AI. Save this as a new template below so future uploads skip AI."
              : "No saved template matches this file's layout — parsed with AI instead. Save this as a new template below if this is a new format for this customer."}
          </div>
        )}

        {aiCorrectedFields && Object.keys(aiCorrectedFields).length > 0 && usedTemplateId && step !== 'select-customer' && step !== 'upload' && (
          <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg">
            <p className="font-semibold text-amber-900 dark:text-amber-100 mb-1">
              AI corrected {Object.keys(aiCorrectedFields).length} field{Object.keys(aiCorrectedFields).length !== 1 ? 's' : ''} the template got wrong
            </p>
            <ul className="text-sm text-amber-800 dark:text-amber-300 mb-3 list-disc list-inside">
              {Object.entries(aiCorrectedFields).map(([field, value]) => (
                <li key={field}>AI corrected <strong>{field.replace(/_/g, ' ')}</strong> to "{String(value)}"</li>
              ))}
            </ul>
            <p className="text-sm text-amber-800 dark:text-amber-300 mb-3">Save to template so next upload works?</p>
            <div className="flex gap-2">
              <button
                onClick={handleSaveAiCorrections}
                disabled={aiCorrectionSaving}
                className="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors"
              >
                {aiCorrectionSaving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => setAiCorrectedFields(null)}
                disabled={aiCorrectionSaving}
                className="px-4 py-2 border border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-300 text-sm font-semibold rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
              >
                Skip
              </button>
            </div>
          </div>
        )}

        {manualFallbackNotice && (
          <div className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-lg text-yellow-800 dark:text-yellow-300">
            ⚠️ {manualFallbackNotice}
          </div>
        )}

        {/* Training Modal Banner - After successful parsing */}
        {step === 'review' && pivotGenerated && editedData && fieldMappingsUsed && (
          <div className="mb-4 p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-2xl">💾</span>
              <div>
                <p className="font-semibold text-purple-900 dark:text-purple-100">Save as Template</p>
                <p className="text-sm text-purple-700 dark:text-purple-400">Help the system recognize this supplier format automatically next time</p>
              </div>
            </div>
            <button
              onClick={handleOpenTrainingModal}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors"
            >
              Create Template
            </button>
          </div>
        )}

        {/* Upload step */}
        {step === 'upload' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Job Ref</label>
              <input type="text" value={jobRef} onChange={e => setJobRef(e.target.value)}
                placeholder="e.g., KI-00123"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Source Type</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" checked={sourceType === 'local'} onChange={() => { setSourceType('local'); setFile(null); setPlFile(null); setDriveFolderUrl('') }}
                    className="w-4 h-4" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Upload Local File</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" checked={sourceType === 'drive'} onChange={() => { setSourceType('drive'); setFile(null); setPlFile(null) }}
                    className="w-4 h-4" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Google Drive Folder</span>
                </label>
              </div>
            </div>

            {sourceType === 'local' && (
              <div>
                <div
                  className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-12 text-center cursor-pointer hover:border-blue-400 transition-colors"
                  onClick={() => fileRef.current?.click()}
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) setFile(f) }}
                >
                  <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  {file ? (
                    <p className="text-blue-600 dark:text-blue-400 font-medium">{file.name}</p>
                  ) : (
                    <>
                      <p className="text-gray-600 dark:text-gray-400 font-medium">Drop PDF or Excel file here or click to browse</p>
                      <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">Commercial invoice (PDF, XLSX, XLS)</p>
                    </>
                  )}
                  <input ref={fileRef} type="file" accept=".pdf,.xlsx,.xls" className="hidden"
                    onChange={e => { const f = e.target.files?.[0]; if (f) setFile(f) }} />
                </div>

                {/* Z42.2: Invoice + Packing List are usually 2 sheets in one workbook,
                    but some shippers send them as 2 separate files -- optional second
                    upload, Excel only (a template's field_mappings is a row/col concept
                    with no PDF equivalent). */}
                {file && getFileType(file) === 'excel' && (
                  <div className="mt-4">
                    <div
                      className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-6 text-center cursor-pointer hover:border-blue-400 transition-colors"
                      onClick={() => plFileRef.current?.click()}
                      onDragOver={e => e.preventDefault()}
                      onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) setPlFile(f) }}
                    >
                      {plFile ? (
                        <div className="flex items-center justify-center gap-2">
                          <p className="text-blue-600 dark:text-blue-400 font-medium">{plFile.name}</p>
                          <button onClick={e => { e.stopPropagation(); setPlFile(null) }} className="text-xs text-gray-500 hover:text-red-600 underline">Remove</button>
                        </div>
                      ) : (
                        <p className="text-gray-500 dark:text-gray-400 text-sm">Optional: add a separate Packing List file (if it's not already a sheet in the file above)</p>
                      )}
                      <input ref={plFileRef} type="file" accept=".xlsx,.xls" className="hidden"
                        onChange={e => { const f = e.target.files?.[0]; if (f) setPlFile(f) }} />
                    </div>
                  </div>
                )}

                {file && (
                  <button onClick={handleExtract}
                    className="mt-4 w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors">
                    Extract & Parse with AI
                  </button>
                )}
              </div>
            )}

            {sourceType === 'drive' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Drive Folder URL</label>
                <input type="url" value={driveFolderUrl} onChange={e => setDriveFolderUrl(e.target.value)}
                  placeholder="https://drive.google.com/drive/folders/..."
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
              </div>
              <button onClick={handleExtractFromDrive} disabled={!driveFolderUrl.trim()}
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold rounded-lg transition-colors">
                Parse Google Drive Folder
              </button>
            </div>
            )}
          </div>
        )}

        {/* Processing steps */}
        {(step === 'extracting' || step === 'parsing' || step === 'generating-pivot') && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-700 dark:text-gray-300 font-medium">{progress || 'Processing...'}</p>
          </div>
        )}

        {/* Review step - Editable */}
        {step === 'review' && editedData && extracted && (
          <div className="space-y-6">
            {/* HS Code rule suggestions */}
            {(() => {
              const activeRules = hsRuleSuggestions.filter(r => !dismissedRules.has(r.id) && editedData.items.some(i => i.hs_code === r.from_hs_code))
              if (activeRules.length === 0) return null
              return (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      <span className="font-semibold text-blue-800 dark:text-blue-200 text-sm">
                        {activeRules.length} HS Code replacement rule{activeRules.length !== 1 ? 's' : ''} found
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        const codeMap = new Map(activeRules.map(r => [r.from_hs_code, r.to_hs_code]))
                        const newItems = editedData.items.map(item =>
                          item.hs_code && codeMap.has(item.hs_code) ? { ...item, hs_code: codeMap.get(item.hs_code)! } : item
                        )
                        setEdited({ ...editedData, items: newItems })
                        setDismissedRules(new Set([...dismissedRules, ...activeRules.map(r => r.id)]))
                      }}
                      className="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors">
                      Accept All ({activeRules.length})
                    </button>
                  </div>
                  {activeRules.map(rule => (
                    <div key={rule.id} className="flex items-center gap-3 text-sm bg-white dark:bg-blue-900/30 rounded-lg p-3 border border-blue-200 dark:border-blue-700">
                      <span className="font-mono font-semibold text-red-700 dark:text-red-400">{rule.from_hs_code}</span>
                      <span className="text-gray-400">→</span>
                      <span className="font-mono font-semibold text-green-700 dark:text-green-400">{rule.to_hs_code}</span>
                      {rule.reason && <span className="text-gray-500 dark:text-gray-400 text-xs">({rule.reason})</span>}
                      {rule.customer && <span className="text-xs text-blue-600 dark:text-blue-400 ml-auto mr-2">for {rule.customer.name}</span>}
                      <div className="flex gap-2 ml-auto">
                        <button
                          onClick={() => {
                            const newItems = editedData.items.map(item =>
                              item.hs_code === rule.from_hs_code ? { ...item, hs_code: rule.to_hs_code } : item
                            )
                            setEdited({ ...editedData, items: newItems })
                            setDismissedRules(new Set([...dismissedRules, rule.id]))
                          }}
                          className="px-2 py-1 text-xs bg-green-600 hover:bg-green-700 text-white rounded transition-colors">
                          Accept
                        </button>
                        <button
                          onClick={() => setDismissedRules(new Set([...dismissedRules, rule.id]))}
                          className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 rounded transition-colors">
                          Ignore
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            })()}

            {/* HS Code error banner */}
            {validations.some(v => !v.valid && editedData.items.some(i => i.hs_code === v.hs_code)) && (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-xl flex items-start gap-3">
                <svg className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="font-semibold text-red-800 dark:text-red-200">Invalid HS Codes — fix before saving</p>
                  <p className="text-sm text-red-700 dark:text-red-300 mt-0.5">
                    {validations.filter(v => !v.valid && editedData.items.some(i => i.hs_code === v.hs_code)).length} code(s) failed HMRC validation. Rows are highlighted below — edit the HS Code cells to correct them.
                  </p>
                </div>
              </div>
            )}

            {/* Header info - Editable */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Invoice Header (Editable)</h2>
                {edited && (
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                      📝 Modified
                    </span>
                    <button
                      onClick={() => setEdited(null)}
                      className="px-3 py-1 text-xs bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded transition-colors"
                    >
                      Reset to AI Values
                    </button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  ['Invoice No', 'invoice_no', editedData.header.invoice_no, 'text' as const],
                  ['Invoice Date', 'invoice_date', editedData.header.invoice_date, 'date' as const],
                  ['Currency', 'currency', editedData.header.currency, 'text' as const],
                  ['Total Amount', 'total_amount', editedData.header.total_amount, 'number' as const],
                  ['Shipper', 'shipper', editedData.header.shipper, 'text' as const],
                  ['Shipper Address', 'shipper_address', editedData.header.shipper_address, 'text' as const],
                  ['Consignee', 'consignee', editedData.header.consignee, 'text' as const],
                  ['Consignee Address', 'consignee_address', editedData.header.consignee_address, 'text' as const],
                  ['Country of Origin', 'country_of_origin', editedData.header.country_of_origin, 'text' as const],
                  ['Incoterms', 'incoterms', editedData.header.incoterms, 'text' as const],
                  ['EORI', 'eori', editedData.header.eori, 'text' as const],
                  ['VAT No', 'vat_no', editedData.header.vat_no, 'text' as const],
                  ['Port of Loading', 'port_of_loading', editedData.header.port_of_loading, 'text' as const],
                  ['Port of Discharge', 'port_of_discharge', editedData.header.port_of_discharge, 'text' as const],
                  ['Gross Weight (kg)', 'gross_weight_kg', editedData.header.gross_weight_kg, 'number' as const],
                  ['Net Weight (kg)', 'net_weight_kg', editedData.header.net_weight_kg, 'number' as const],
                  ['Packages', 'packages', editedData.header.packages, 'number' as const],
                ].map(([label, field, val, inputType]) => (
                  <div key={field} className="text-sm">
                    <label className="text-gray-600 dark:text-gray-400 font-medium block mb-1">{label}</label>
                    <input
                      type={inputType as 'text' | 'date' | 'number'}
                      value={val ?? ''}
                      readOnly={field === 'total_amount' || field === 'gross_weight_kg' || field === 'net_weight_kg'}
                      title={field === 'gross_weight_kg' || field === 'net_weight_kg' || field === 'total_amount' ? 'Sum of the lines, each rounded to 2 dp (edit the lines)' : undefined}
                      onChange={e => updateHeader(field as keyof InvoiceHeader, e.target.value ? (inputType === 'number' ? Number(e.target.value) : e.target.value) : null)}
                      placeholder={inputType === 'date' ? 'YYYY-MM-DD' : ''}
                      className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                        field === 'country_of_origin' && val && !isValidIsoCountry(String(val))
                          ? 'border-red-500 bg-red-50 dark:bg-red-900/30'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                    />
                    {field === 'country_of_origin' && val && !isValidIsoCountry(String(val)) && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">Not a valid 2-letter country code (e.g. CN) — please correct</p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Line items - Editable */}
            {editedData.items && editedData.items.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Line Items (Editable) ({editedData.items.length})</h2>
                  <button
                    onClick={addItem}
                    className="px-3 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                  >
                    + Add Item
                  </button>
                </div>
                <div className="overflow-x-auto" {...itemsScrollProps}>
                  <table ref={itemsTableRef as React.RefObject<HTMLTableElement>} className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        {['Description','HS Code','Pcs','Ctns','Gross kg','Net kg','Unit Value','Amount','Action'].map(h => (
                          <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {editedData.items.map((item, idx) => {
                        const isHsInvalid = validations.some(v => !v.valid && item.hs_code && item.hs_code === v.hs_code)
                        return (
                        <tr key={idx} className={isHsInvalid ? 'bg-red-50 dark:bg-red-900/30' : ''}>
                          {/* U3: a single-line input can't wrap, so a long description forced this
                              column (and the whole table) as wide as its longest line -- capping the
                              width and switching to a 2-row textarea wraps it instead, which is most
                              of why the table needed horizontal scroll to see the rightmost columns. */}
                          <td className="px-4 py-3 max-w-[220px]">
                            <textarea rows={2} value={item.goods_description || ''} onChange={e => updateItem(idx, 'goods_description', e.target.value)} className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm resize-none" />
                          </td>
                          <td className={`px-4 py-3 ${isHsInvalid ? 'bg-red-100 dark:bg-red-900/40' : isLowConfidence(item.hs_code_confidence) ? 'bg-yellow-50 dark:bg-yellow-900/20' : ''}`}>
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                value={item.hs_code || ''}
                                onChange={e => updateItem(idx, 'hs_code', e.target.value || null)}
                                onFocus={() => { hsInputFocusRef.current = item.hs_code }}
                                onBlur={e => {
                                  const newCode = e.target.value.trim()
                                  const oldCode = hsInputFocusRef.current
                                  if (!oldCode || !newCode || newCode === oldCode) return

                                  // Bulk update: find other rows still using the same old code
                                  const others = editedData
                                    ? editedData.items
                                        .map((it, i) => ({ it, i }))
                                        .filter(({ it, i }) => i !== idx && it.hs_code === oldCode)
                                        .map(({ i }) => i)
                                    : []

                                  if (others.length > 0) {
                                    setBulkHsPrompt({ oldCode, newCode, matchingIndices: others, sourceIdx: idx })
                                  } else {
                                    setSaveRulePrompt({ from: oldCode, to: newCode })
                                    setSaveRuleReason('')
                                  }
                                }}
                                placeholder="e.g. 621790"
                                className={`flex-1 px-2 py-1 border rounded text-gray-900 dark:text-white font-mono text-sm ${isHsInvalid ? 'border-red-500 bg-red-50 dark:bg-red-900/20 dark:border-red-500' : isLowConfidence(item.hs_code_confidence) ? 'border-yellow-400 bg-yellow-50 dark:bg-yellow-900/10 dark:border-yellow-600' : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700'}`}
                              />
                              {isHsInvalid && (
                                <svg className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                              )}
                              {!isHsInvalid && isLowConfidence(item.hs_code_confidence) && (
                                <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <input type="number" value={item.pcs ?? ''} onChange={e => updateItem(idx, 'pcs', e.target.value ? Number(e.target.value) : null)} className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                value={item.ctns ?? ''}
                                onChange={e => updateItem(idx, 'ctns', e.target.value ? Number(e.target.value) : null)}
                                title={item.ctns_original != null ? `原值为 ${item.ctns_original}，已自动调为 ${item.ctns}` : undefined}
                                className={`w-20 px-2 py-1 border rounded text-gray-900 dark:text-white text-sm ${
                                  item.ctns != null && item.ctns < 0 ? 'border-red-500 bg-white dark:bg-gray-700'
                                    : item.ctns_original != null ? 'border-violet-500 bg-violet-100 dark:bg-violet-900/40'
                                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700'
                                }`}
                              />
                              {item.ctns != null && item.ctns < 0 && (
                                <span className="text-red-600 dark:text-red-400 text-xs" title="Negative cartons -- must be fixed before this invoice can be saved">✕</span>
                              )}
                              {item.ctns != null && item.ctns >= 0 && roundCartonsForCustoms(item.ctns).wasAdjusted && (
                                <span
                                  className="text-amber-500 dark:text-amber-400 text-sm cursor-help"
                                  title={`Original ${item.ctns} rounded up to ${roundCartonsForCustoms(item.ctns).adjusted} for customs compliance (UK Customs requires >=1 whole carton per line)`}
                                >
                                  ⚠
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <input type="number" value={item.gross_weight ?? ''} onChange={e => updateItem(idx, 'gross_weight', e.target.value ? Number(e.target.value) : null)} className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
                          </td>
                          <td className="px-4 py-3">
                            <input type="number" value={item.net_weight ?? ''} onChange={e => updateItem(idx, 'net_weight', e.target.value ? Number(e.target.value) : null)} className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
                          </td>
                          <td className="px-4 py-3">
                            <input type="number" step="0.01" value={item.unit_value ?? ''} onChange={e => updateItem(idx, 'unit_value', e.target.value ? Number(e.target.value) : null)} className="w-24 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
                          </td>
                          <td className="px-4 py-3">
                            <input type="number" step="0.01" value={item.amount ?? ''} onChange={e => updateItem(idx, 'amount', e.target.value ? Number(e.target.value) : null)} className="w-24 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
                          </td>
                          <td className="px-4 py-3">
                            <button onClick={() => removeItem(idx)} className="px-2 py-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded text-sm">Delete</button>
                          </td>
                        </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
                <StickyHScrollbar {...itemsScrollBar} />
              </div>
            )}

            {/* Pivot Table - Auto-calculated Summary */}
            {pivot.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Pivot Summary (Auto-calculated)</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Grouped by HS Code - updates automatically as you edit line items</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        {['HS Code', 'Description', 'PCS', 'CTNS', 'Gross (kg)', 'Net (kg)', 'Amount'].map(h => (
                          <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {pivot.map((item, idx) => (
                        <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-4 py-3 font-mono text-gray-900 dark:text-white">{item.hs_code || '—'}</td>
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{item.goods_description}</td>
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{item.pcs}</td>
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{item.ctns}</td>
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{item.gross_weight.toFixed(2)}</td>
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{item.net_weight.toFixed(2)}</td>
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">{item.amount.toFixed(2)}</td>
                        </tr>
                      ))}
                      <tr className="bg-blue-50 dark:bg-blue-900/20 font-semibold">
                        <td colSpan={2} className="px-4 py-3 text-gray-900 dark:text-white">TOTALS</td>
                        <td className="px-4 py-3 text-gray-900 dark:text-white">{pivot.reduce((sum, item) => sum + item.pcs, 0)}</td>
                        <td className="px-4 py-3 text-gray-900 dark:text-white">{pivot.reduce((sum, item) => sum + item.ctns, 0)}</td>
                        <td className="px-4 py-3 text-gray-900 dark:text-white">{pivot.reduce((sum, item) => sum + item.gross_weight, 0).toFixed(2)}</td>
                        <td className="px-4 py-3 text-gray-900 dark:text-white">{pivot.reduce((sum, item) => sum + item.net_weight, 0).toFixed(2)}</td>
                        <td className="px-4 py-3 text-gray-900 dark:text-white">{pivot.reduce((sum, item) => sum + item.amount, 0).toFixed(2)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Z98: header-as-stated vs declared line totals, with the reason spelled out */}
            {editedData && (() => {
              const diffs = ciDiscrepancies(editedData.header, editedData.items)
              if (diffs.length === 0) return null
              return (
                <div className="bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-xl p-4 text-sm text-gray-700 dark:text-gray-300 space-y-1">
                  {diffs.map(d => <div key={d.label}>ℹ {d.note}</div>)}
                </div>
              )
            })()}

            {/* Total Verification Panel */}
            {pivotGenerated && (
              <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border overflow-hidden ${totalMismatch ? 'border-red-200 dark:border-red-800' : 'border-green-200 dark:border-green-800'}`}>
                <div className={`px-6 py-4 border-b ${totalMismatch ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20' : 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20'}`}>
                  <div className="flex items-center gap-2">
                    {totalMismatch ? (
                      <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    )}
                    <h2 className={`text-lg font-semibold ${totalMismatch ? 'text-red-900 dark:text-red-100' : 'text-green-900 dark:text-green-100'}`}>
                      Total Verification
                    </h2>
                  </div>
                </div>
                <div className="p-6">
                  {totalMismatch && (
                    <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded text-red-800 dark:text-red-200 text-sm">
                      ⚠️ Total mismatch detected in: {totalMismatch}. Please verify the data before saving.
                    </div>
                  )}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className={totalMismatch ? 'bg-red-50 dark:bg-red-900/10' : 'bg-green-50 dark:bg-green-900/10'}>
                        <tr>
                          {['Field', 'Source Total', 'Extracted Total', 'Match?'].map(h => (
                            <th key={h} className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${totalMismatch ? 'text-red-700 dark:text-red-300' : 'text-green-700 dark:text-green-300'}`}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className={totalMismatch ? 'divide-y divide-red-100 dark:divide-red-900' : 'divide-y divide-green-100 dark:divide-green-900'}>
                        {editedData && [
                          { label: 'Total PCS', source: editedData.items.reduce((sum, item) => sum + (item.pcs || 0), 0), extracted: pivot.reduce((sum, item) => sum + (item.pcs || 0), 0) },
                          { label: 'Total CTNS', source: editedData.items.reduce((sum, item) => sum + (item.ctns || 0), 0), extracted: pivot.reduce((sum, item) => sum + (item.ctns || 0), 0) },
                          { label: 'Total GW (kg)', source: editedData.items.reduce((sum, item) => sum + (item.gross_weight || 0), 0), extracted: pivot.reduce((sum, item) => sum + (item.gross_weight || 0), 0) },
                          { label: 'Total NW (kg)', source: editedData.items.reduce((sum, item) => sum + (item.net_weight || 0), 0), extracted: pivot.reduce((sum, item) => sum + (item.net_weight || 0), 0) },
                          { label: 'Total Amount', source: editedData.items.reduce((sum, item) => sum + (item.amount || 0), 0), extracted: pivot.reduce((sum, item) => sum + (item.amount || 0), 0) },
                        ].map((row) => {
                          const match = Math.abs(row.source - row.extracted) < 0.01
                          return (
                            <tr key={row.label} className={match ? 'bg-green-50 dark:bg-green-900/10' : 'bg-red-50 dark:bg-red-900/10'}>
                              <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{row.label}</td>
                              <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{typeof row.source === 'number' ? row.source.toFixed(2) : row.source}</td>
                              <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{typeof row.extracted === 'number' ? row.extracted.toFixed(2) : row.extracted}</td>
                              <td className="px-4 py-3">
                                {match ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200">
                                    ✅ Match
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200">
                                    ⚠️ Differs
                                  </span>
                                )}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Use CYUK DAN Toggle */}
            {pivotGenerated && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">CDS Export Settings</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Choose export configuration</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-3">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Use CYUK DAN?</label>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setUseCyukDan(false)}
                          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                            !useCyukDan
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                          }`}
                        >
                          No
                        </button>
                        <button
                          onClick={() => setUseCyukDan(true)}
                          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                            useCyukDan
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                          }`}
                        >
                          Yes
                        </button>
                      </div>
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      {useCyukDan ? '33 columns with DAN codes' : '23 columns without DAN codes'}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* HS Code Pivot Table - Auto-calculated */}
            {pivotGenerated && pivot?.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-blue-200 dark:border-blue-800 overflow-hidden">
                <div className="px-6 py-4 border-b border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-blue-900 dark:text-blue-100">CDS Pivot — by HS Code (Auto-calculated)</h2>
                    <p className="text-sm text-blue-600 dark:text-blue-400 mt-0.5">{pivot.length} commodity code{pivot.length !== 1 ? 's' : ''}</p>
                  </div>
                  <button
                    onClick={handleExportCDS}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export CDS Excel
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-blue-50 dark:bg-blue-900/10">
                      <tr>
                        {['HS Code','Description','Total Pcs','Total Ctns','Gross kg','Net kg','Total Value'].map(h => (
                          <th key={h} className="px-4 py-3 text-left text-xs font-medium text-blue-700 dark:text-blue-300 uppercase tracking-wider">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-blue-100 dark:divide-blue-900">
                      {pivot.map((row, idx) => (
                        <tr key={idx} className="hover:bg-blue-50 dark:hover:bg-blue-900/10">
                          <td className="px-4 py-3 font-mono font-semibold text-blue-800 dark:text-blue-200">{row.hs_code || 'UNKNOWN'}</td>
                          <td className="px-4 py-3 text-gray-900 dark:text-white">{row.goods_description}</td>
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{row.pcs}</td>
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{row.ctns}</td>
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{row.gross_weight?.toFixed(2)}</td>
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{row.net_weight?.toFixed(2)}</td>
                          <td className="px-4 py-3 font-semibold text-gray-900 dark:text-white">{row.amount?.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Action buttons */}
            {(() => {
              const hasInvalidHs = validations.some(v => !v.valid && editedData.items.some(i => i.hs_code === v.hs_code))
              return (
                <div className="flex gap-4">
                  <button onClick={() => setStep('upload')}
                    className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                    Back
                  </button>
                  <button onClick={() => handleSave(true)} disabled={hasInvalidHs}
                    title={hasInvalidHs ? 'Fix all invalid HS codes before saving' : undefined}
                    className={`flex-1 py-3 font-semibold rounded-lg transition-colors ${hasInvalidHs ? 'bg-gray-400 cursor-not-allowed text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}>
                    {hasInvalidHs ? 'Fix HS Code Errors to Save' : 'Save Invoice to Database'}
                  </button>
                </div>
              )
            })()}
          </div>
        )}

        {/* Bulk HS code update prompt */}
        {bulkHsPrompt && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-xl max-w-sm w-full p-6 shadow-xl">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Update all matching rows?</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                <span className="font-semibold text-gray-900 dark:text-white">{bulkHsPrompt.matchingIndices.length} other row{bulkHsPrompt.matchingIndices.length !== 1 ? 's' : ''}</span> also use HS Code{' '}
                <span className="font-mono font-semibold text-red-700 dark:text-red-400">{bulkHsPrompt.oldCode}</span>. Update all to{' '}
                <span className="font-mono font-semibold text-green-700 dark:text-green-400">{bulkHsPrompt.newCode}</span>?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setBulkHsPrompt(null)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm">
                  This row only
                </button>
                <button
                  onClick={() => {
                    if (editedData) {
                      const { matchingIndices, newCode } = bulkHsPrompt
                      const newItems = editedData.items.map((item, i) =>
                        matchingIndices.includes(i) ? { ...item, hs_code: newCode } : item
                      )
                      setEdited({ ...editedData, items: newItems })
                    }
                    const { oldCode: bulkOldCode, newCode: bulkNewCode } = bulkHsPrompt
                    setBulkHsPrompt(null)
                    setSaveRulePrompt({ from: bulkOldCode, to: bulkNewCode })
                    setSaveRuleReason('')
                  }}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors text-sm">
                  Update all ({bulkHsPrompt.matchingIndices.length + 1} rows)
                </button>
              </div>
            </div>
          </div>
        )}

        {/* HS Rules apply modal — auto-appears after parse when matching rules found */}
        {showRulesModal && editedData && (() => {
          const pending = hsRuleSuggestions.filter(r => editedData.items.some(i => i.hs_code === r.from_hs_code))
          if (pending.length === 0) { setShowRulesModal(false); return null }
          return (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
              <div className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full p-6 shadow-xl">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">HS Code Replacement Rules Found</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">The following rules apply to HS codes in this invoice. Apply them now?</p>
                <div className="space-y-2 mb-5 max-h-64 overflow-y-auto">
                  {pending.map(rule => (
                    <div key={rule.id} className="flex items-center gap-3 text-sm bg-gray-50 dark:bg-gray-700 rounded-lg px-3 py-2">
                      <span className="font-mono font-semibold text-red-700 dark:text-red-400">{rule.from_hs_code}</span>
                      <span className="text-gray-400">→</span>
                      <span className="font-mono font-semibold text-green-700 dark:text-green-400">{rule.to_hs_code}</span>
                      {rule.reason && <span className="text-gray-500 dark:text-gray-400 text-xs truncate">({rule.reason})</span>}
                      {rule.customer && <span className="text-xs text-blue-600 dark:text-blue-400 ml-auto flex-shrink-0">for {rule.customer.name}</span>}
                    </div>
                  ))}
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowRulesModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm">
                    Skip for now
                  </button>
                  <button
                    onClick={() => {
                      if (editedData) {
                        const codeMap = new Map(pending.map(r => [r.from_hs_code, r.to_hs_code]))
                        const newItems = editedData.items.map(item =>
                          item.hs_code && codeMap.has(item.hs_code) ? { ...item, hs_code: codeMap.get(item.hs_code)! } : item
                        )
                        setEdited({ ...editedData, items: newItems })
                        setDismissedRules(new Set([...dismissedRules, ...pending.map(r => r.id)]))
                      }
                      setShowRulesModal(false)
                    }}
                    className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors text-sm">
                    Apply all ({pending.length})
                  </button>
                </div>
              </div>
            </div>
          )
        })()}

        {/* Save-as-rule prompt modal */}
        {saveRulePrompt && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full p-6 shadow-xl">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Save as Replacement Rule?</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                You changed HS code <span className="font-mono font-semibold text-red-700 dark:text-red-400">{saveRulePrompt.from}</span> → <span className="font-mono font-semibold text-green-700 dark:text-green-400">{saveRulePrompt.to}</span>.
                Save this as a global replacement rule so future invoices get the suggestion automatically?
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reason (optional)</label>
                <input type="text" value={saveRuleReason} onChange={e => setSaveRuleReason(e.target.value)}
                  placeholder="e.g. Customer instructed replacement"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
              </div>
              <div className="flex gap-3">
                <button onClick={() => setSaveRulePrompt(null)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm">
                  Skip
                </button>
                <button
                  onClick={async () => {
                    try {
                      await createHsRule({ customer_id: null, from_hs_code: saveRulePrompt.from, to_hs_code: saveRulePrompt.to, reason: saveRuleReason || null })
                    } catch { /* ignore save failure */ }
                    setSaveRulePrompt(null)
                  }}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors text-sm">
                  Save Rule
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Ambiguous template match -- 2+ of this customer's templates scored
            a close match against this file; staff must pick one (or AI). */}
        {ambiguousCandidates && ambiguousCandidates.length > 0 && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full p-6 shadow-xl">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">This file matches these templates, pick one:</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {ambiguousCandidates.length} saved templates for this customer scored a similarly close match against this file's layout.
              </p>
              <div className="space-y-2 mb-4">
                {ambiguousCandidates.map(t => (
                  <button
                    key={t.id}
                    onClick={() => resolveAmbiguousTemplate({ templateId: t.id! })}
                    className="w-full text-left px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                  >
                    <span className="font-medium text-gray-900 dark:text-white">{t.name}</span>
                  </button>
                ))}
              </div>
              <button
                onClick={() => resolveAmbiguousTemplate({ forceAI: true })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm"
              >
                None of these, AI parse it
              </button>
            </div>
          </div>
        )}

        {/* Template-fix suggestion prompt -- template-replay path only, shown
            when an edited header field either has label noise around the
            value (e.g. "INVOICE NO. : 2026-MF062603" -- a strip rule) or was
            mapped to the wrong cell entirely (a relocate -- the corrected
            value was found elsewhere in the sheet). */}
        {stripSuggestions && stripSuggestions.length > 0 && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full p-6 shadow-xl">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Save this fix to the template?</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                The way {stripSuggestions.length === 1 ? 'this field' : 'these fields'} was read from this customer's file needs correcting. Save the fix so future uploads read it correctly automatically.
              </p>
              <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
                {stripSuggestions.map(s => (
                  <div key={s.field} className="text-xs bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                    <div className="font-semibold text-gray-700 dark:text-gray-300 capitalize mb-1">{s.label}</div>
                    {s.kind === 'relocate'
                      ? <div className="text-gray-500 dark:text-gray-400">Re-point the template to the cell where the correct value actually is (row {s.row! + 1}, col {s.col! + 1})</div>
                      : <>
                          {s.strip_before && <div className="text-gray-500 dark:text-gray-400">Strip before: <span className="font-mono">{s.strip_before}</span></div>}
                          {s.strip_after && <div className="text-gray-500 dark:text-gray-400">Strip after: <span className="font-mono">{s.strip_after}</span></div>}
                        </>
                    }
                  </div>
                ))}
              </div>
              <div className="flex gap-3">
                <button onClick={() => setStripSuggestions(null)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm">
                  Cancel
                </button>
                <button onClick={() => resolveStripSuggestions(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm">
                  Skip &amp; Save
                </button>
                <button onClick={() => resolveStripSuggestions(true)}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors text-sm">
                  Save Fix &amp; Save
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Duplicate check step */}
        {step === 'duplicate-check' && duplicate && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-yellow-200 dark:border-yellow-800 p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="flex-shrink-0">
                <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-yellow-900 dark:text-yellow-100">Invoice Already Exists</h3>
                <p className="text-yellow-700 dark:text-yellow-400 mt-1">Invoice <strong>{duplicate.invoice_no}</strong> is already in the database (created {new Date(duplicate.created_at).toLocaleDateString()}).</p>
              </div>
            </div>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg mb-6">
              <p className="text-sm text-yellow-800 dark:text-yellow-300">What would you like to do?</p>
            </div>
            <div className="flex gap-4">
              <button onClick={() => setStep('review')}
                className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                Back to Review
              </button>
              <button onClick={() => handleSave(true)}
                className="flex-1 py-3 bg-yellow-600 hover:bg-yellow-700 text-white font-semibold rounded-lg transition-colors">
                Overwrite Existing Invoice
              </button>
              <button onClick={() => {
                setDuplicate(null)
                setStep('saving')
                handleSave(true)
              }}
                className="flex-1 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors">
                Save as New Invoice
              </button>
            </div>
          </div>
        )}

        {/* Saving step */}
        {step === 'saving' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-700 dark:text-gray-300 font-medium">Saving invoice...</p>
          </div>
        )}

        {/* Done step */}
        {step === 'done' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-xl font-semibold text-gray-900 dark:text-white">Invoice saved!</p>
            <p className="text-gray-500 mt-2">Redirecting to detail view...</p>
          </div>
        )}

        {/* Training Modal */}
        {showTrainingModal && editedData && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-xl max-w-2xl w-full p-6 shadow-xl">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">Create Invoice Template</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">Save this supplier's format for automatic detection next time</p>

              {trainingError && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
                  {trainingError}
                </div>
              )}

              <div className="space-y-6">
                {/* Customer link */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Customer
                  </label>
                  <div className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-white font-medium">
                    {selectedCustomer ? `${selectedCustomer.code} – ${selectedCustomer.name}` : '—'}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    This template will be linked to this customer — future uploads from them skip AI.
                  </p>
                </div>

                {/* Field Mapping Preview */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Field Mapping Preview
                  </label>
                  {fieldMappingsUsed ? (
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 text-sm max-h-48 overflow-y-auto">
                      <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-all">
                        {JSON.stringify(fieldMappingsUsed, null, 2)}
                      </pre>
                    </div>
                  ) : (
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 text-sm text-gray-500 dark:text-gray-400">
                      This upload used an existing template — no new field mapping to save. Edit values above and re-upload a fresh file for this customer if the mapping itself needs correcting.
                    </div>
                  )}
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Row/col positions the AI reported each field at in this file — cached so future uploads from this customer skip AI entirely.
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 mt-6">
                <button
                  onClick={() => setShowTrainingModal(false)}
                  disabled={trainingSaving}
                  className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveTemplate}
                  disabled={trainingSaving || !fieldMappingsUsed}
                  className="flex-1 px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {trainingSaving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <span>💾</span>
                      Save Template
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
